import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  DatePicker,
  Input,
  Upload,
  Row,
  Col,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import styled from "styled-components";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo } from "redux/selectors";
import { STATUS_CODE } from "common/Constants";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import { useTranslation } from "react-i18next";

const ModalWrap = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
  }
  .ant-modal-body {
    padding: 0px 24px 24px 24px;
  }
  .custom-upload .ant-upload {
    width: 100%;
  }

  .upload-box {
    width: 100%;
    height: 42px;
    border-radius: 12px;
    background: #cfd6ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #cfd6ff;
  }

  .upload-box:hover {
    background: #bcc6ff;
  }
`;

const { TextArea } = Input;

const ApplyWorkFromHome = ({
  open,
  onCancel,
  data = {},
  getData = () => {},
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const userInfo = useSelector(getUserInfo);
  const [loading, setLoading] = useState(false);
  const [initialValues] = useState({});
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    if (data && Object.keys(data).length) {
      form.setFieldsValue({
        startdate: data?.wfhStartDate ? dayjs(data.wfhStartDate) : null,
        enddate: data?.wfhEndDate ? dayjs(data.wfhEndDate) : null,
        noofdays: data?.wfhNoofDays || "",
        reason: data?.wfhReason || "",
        attachment: data?.fileName
          ? [
              {
                uid: "-1",
                name: data.fileName,
                status: "done",
                url: data.fileUrl || "",
              },
            ]
          : [],
      });
    }
  }, [data]);

  const handleFinish = async (values) => {
    try {
      setLoading(true);

      const fileList = values?.attachment || [];
      const fileObj = fileList?.[0]?.originFileObj || null;

      let uploadedKey = data?.objectKey || "";
      let fileName = data?.fileName || "";

      if (fileList.length === 0) {
        uploadedKey = "";
        fileName = "";
      }

      if (fileObj) {
        const videoTypes = [
          "video/mp4",
          "video/mpeg",
          "video/quicktime",
          "video/x-msvideo",
          "video/x-ms-wmv",
          "video/webm",
          "video/ogg",
        ];

        if (
          videoTypes.includes(fileObj.type) ||
          fileObj.type?.startsWith("video/")
        ) {
          message.error(t("Video files are not allowed"));
          return;
        }

        uploadedKey = await handleDocumentSubmit(fileObj);
        fileName = fileObj.name;
        if (!uploadedKey) return;
      }

      const payload = {
        wfhStartDate: values.startdate.format("YYYY-MM-DD"),
        wfhEndDate: values.enddate.format("YYYY-MM-DD"),
        wfhNoofDays: String(values.noofdays),
        wfhReason: values.reason,
        reportingManager: userInfo?.reportingmanager,
        objectKey: uploadedKey || "",
        fileName: fileName,
        statusId: data?.requestId ? data?.statusId : "0",
      };

      if (data?.requestId) {
        payload.rejectReason = "";
      }

      const url = data?.requestId
        ? `employeerequest/${userInfo?.userid}/${data?.requestId}?requestType=wfhrequest`
        : `employeerequest/wfhrequest`;

      const method = data?.requestId ? "patch" : "post";

      const { code } = await CALL_API(url, method, payload);

      if (code === STATUS_CODE.SUCCESS) {
        await getData();
        setPopupOpen(true);

        setTimeout(() => {
          setPopupOpen(false);
          form.resetFields();
          onCancel();
        }, 2000);
      } else if (code === STATUS_CODE.RECORD_EXIST) {
        message.error(t("WFH request already exists"));
      }
    } catch (err) {
      message.error(t("Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentSubmit = async (file) => {
    try {
      const initPayload = {
        filename: file.name,
        type: file.type,
      };

      const res = await CALL_API(`employeerequest/file`, "POST", initPayload);

      if (res?.code !== STATUS_CODE.SUCCESS) {
        throw new Error("Failed to get upload url");
      }

      const uploadUrl = res?.url;
      const key = res?.key;

      if (!uploadUrl) throw new Error("Upload URL missing");

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (!putRes.ok) throw new Error("Upload failed");

      return key;
    } catch (err) {
      message.error(t("File upload failed"));
      return "";
    }
  };

  const handleStartDateChange = (date) => {
    const endDate = form.getFieldValue("enddate");
    if (date && endDate && date.isAfter(endDate)) {
      form.setFieldsValue({ enddate: date });
    }
    calculateDays();
  };

  const calculateDays = () => {
    const start = form.getFieldValue("startdate");
    const end = form.getFieldValue("enddate");

    if (start && end) {
      const diff = end.diff(start, "day") + 1;
      form.setFieldsValue({ noofdays: diff > 0 ? diff : 0 });
    } else {
      form.setFieldsValue({ noofdays: 0 });
    }
  };

  return (
    <>
      <ModalWrap
        title={
          data?.requestId ? t("Editing WFH Request") : t("Applying for WFH")
        }
        open={open}
        footer={null}
        centered
        width={580}
        onCancel={() => {
          form.resetFields();
          onCancel();
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleFinish}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t("Start Date")}
                name="startdate"
                rules={[
                  { required: true, message: t("Start date is required") },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  onChange={handleStartDateChange}
                  disabledDate={(c) => c && c < dayjs().startOf("day")}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t("End Date")}
                name="enddate"
                rules={[{ required: true, message: t("End date is required") }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  onChange={calculateDays}
                  disabledDate={(current) => {
                    const startDate = form.getFieldValue("startdate");
                    if (!startDate) return false;
                    return current && current < dayjs(startDate).startOf("day");
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t("No. of days")} name="noofdays">
            <Input disabled />
          </Form.Item>

          <Form.Item
            label={t("Reason for WFH")}
            name="reason"
            rules={[
              { required: true, message: t("Reason is required") },
              { max: 250, message: t("Max 250 chars") },
            ]}
          >
            <TextArea rows={4} maxLength={250} />
          </Form.Item>

          <Form.Item
            label={t("Attachment (Optional)")}
            name="attachment"
            valuePropName="fileList"
            getValueFromEvent={(e) => e?.fileList}
          >
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              className="custom-upload"
            >
              {!form.getFieldValue("attachment")?.length && (
                <div className="upload-box">
                  <UploadOutlined />
                  <span style={{ marginLeft: 8 }}>{t("Upload File")}</span>
                </div>
              )}
            </Upload>
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <ButtonOutlined
              onClick={() => {
                form.resetFields();
                onCancel();
              }}
            >
              {t("Cancel")}
            </ButtonOutlined>

            <ButtonFilled type="primary" htmlType="submit" loading={loading}>
              {data?.requestId ? t("Save Changes") : t("Submit")}
            </ButtonFilled>
          </div>
        </Form>
      </ModalWrap>

      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type={"success"}
        message={data?.requestId ? t("Saved") : t("Submitted")}
      />
    </>
  );
};

export default ApplyWorkFromHome;
